/**
 * The header station chip (ADR-44): `Bridget · Checkout ▾`. Clicking it is
 * how staff move between stations - the universal navigation of Sale Day
 * Mode, replacing scattered links between employee interfaces. No logout, no
 * new MFA: same session, different verified hat (the target URL pins
 * `mode` + `as`, and the server re-verifies the role on every request).
 *
 * A page with an in-flight operation passes `canLeave` returning a refusal
 * message - a half-taken check must be finished or cancelled, never silently
 * abandoned by a station switch.
 */

import { el, setChildren } from './dom.js';
import { availableModes, modeHref, type WorkMode } from './work-modes.js';
import type { Session } from './session.js';

export function mountModeSwitcher(opts: {
  session: Session;
  container: HTMLElement;
  current: WorkMode;
  canLeave?: () => string | null;
  say?: (msg: string) => void;
}): void {
  const { session, container, current } = opts;
  // The popup is absolutely positioned, so it anchors to the nearest
  // positioned ancestor. Pages mount this into whatever element they like and
  // should not have to know that; setting it here means a new station gets a
  // correctly placed dropdown without copying a CSS rule it cannot see.
  container.classList.add('ms-wrap');
  const modes = availableModes(session.roles);
  const label = `${session.actorId} · ${current.label}`;
  if (modes.length <= 1) {
    setChildren(container, el('span', { class: 'ms-chip ms-static', text: label }));
    return;
  }

  const chip = el('button', { class: 'ms-chip', text: `${label} ▾`, attrs: { 'aria-haspopup': 'true', 'aria-expanded': 'false' } });
  const pop = el('div', { class: 'ms-pop' },
    el('div', { class: 'ms-title', text: 'Switch station' }),
    ...modes.map((m) => {
      const item = el('button', { class: `ms-item${m.key === current.key ? ' on' : ''}` },
        el('span', { class: 'ms-dot', text: m.key === current.key ? '●' : '○' }),
        el('span', {}, el('span', { class: 'ms-name', text: m.label }), el('span', { class: 'ms-desc', text: m.desc })),
      );
      item.addEventListener('click', () => switchTo(m));
      return item;
    }),
  );
  pop.style.display = 'none';

  function close(): void { pop.style.display = 'none'; chip.setAttribute('aria-expanded', 'false'); }
  function switchTo(m: WorkMode): void {
    if (m.key === current.key) { close(); return; }
    const refusal = opts.canLeave?.() ?? null;
    if (refusal) { close(); opts.say?.(refusal); return; }
    location.href = modeHref(m, location.search, session.roles);
  }

  chip.addEventListener('click', (e) => {
    e.stopPropagation();
    const open = pop.style.display !== 'none';
    pop.style.display = open ? 'none' : 'block';
    chip.setAttribute('aria-expanded', open ? 'false' : 'true');
  });
  document.addEventListener('click', (e) => { if (!pop.contains(e.target as Node)) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && pop.style.display !== 'none') close(); });

  container.classList.add('ms-wrap');
  setChildren(container, chip, pop);
}
